import { AxiosError } from 'axios';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { filterHiddenPlaceholderRotations, getMatchedLegRunIdsInRow, VehicleRowGroup } from './adapter';
import { dropServerExistingRotations, mergeExtraRotations, nextRotationNumber } from './rotationEditing';
import { VehicleRowVM } from './vehicleRowVM';

type UseRotationEditingParams = {
  sourceRowsRaw: VehicleRowVM[];
  getRowGroups: () => VehicleRowGroup[];
  resetAllKey: string;
  hiddenResetKey: string;
  resetLegRuns: (legRunIds: number[]) => Promise<void>;
  confirm: (opts: { title: string; description: string; onConfirm: () => void | Promise<void> }) => void;
  notify: (message: string, variant: 'success' | 'error') => void;
};

type UseRotationEditingResult = {
  sourceRows: VehicleRowVM[];
  applyHiddenFilter: (rowGroups: VehicleRowGroup[]) => VehicleRowGroup[];
  /** 回転は勤務 (delivery) の中の概念。追加先・削除先は勤務で決める */
  handleAddRotation: (deliveryId: number) => void;
  handleDeleteRotation: (deliveryId: number, rotationNumber: number) => void;
};

export function useRotationEditing({
  sourceRowsRaw, getRowGroups, resetAllKey, hiddenResetKey, resetLegRuns, confirm, notify,
}: UseRotationEditingParams): UseRotationEditingResult {
  const [extraRotations, setExtraRotations] = useState<Map<number, number[]>>(new Map());
  const [hiddenPlaceholderRotations, setHiddenPlaceholderRotations] = useState<Map<number, Set<number>>>(new Map());

  // 対象日変化: extra + hidden の両方をリセット（convenience Presenter.tsx:600）。
  useEffect(() => {
    setExtraRotations(new Map());
    setHiddenPlaceholderRotations(new Map());
  }, [resetAllKey]);

  // 固定ルート変化: hidden のみリセット（convenience Presenter.tsx:618）。
  useEffect(() => {
    setHiddenPlaceholderRotations(new Map());
  }, [hiddenResetKey]);

  // サーバ既存回転を extra から落とす（convenience Presenter.tsx:624-644）。
  useEffect(() => {
    setExtraRotations((prev) => dropServerExistingRotations(prev, sourceRowsRaw));
  }, [sourceRowsRaw]);

  const sourceRows = useMemo(
    () => mergeExtraRotations(sourceRowsRaw, extraRotations),
    [sourceRowsRaw, extraRotations],
  );

  const applyHiddenFilter = useCallback(
    (rowGroups: VehicleRowGroup[]) => filterHiddenPlaceholderRotations(rowGroups, hiddenPlaceholderRotations),
    [hiddenPlaceholderRotations],
  );

  const handleAddRotation = useCallback((deliveryId: number) => {
    setExtraRotations((prev) => {
      const next = new Map(prev);
      const existing = next.get(deliveryId) ?? [];
      next.set(deliveryId, [...existing, nextRotationNumber(sourceRowsRaw, existing, deliveryId)]);
      return next;
    });
  }, [sourceRowsRaw]);

  const handleDeleteRotation = useCallback((deliveryId: number, rotationNumber: number) => {
    // 仮想（ローカル追加）は state から除去。
    if ((extraRotations.get(deliveryId) ?? []).includes(rotationNumber)) {
      setExtraRotations((prev) => {
        const next = new Map(prev);
        const filtered = (next.get(deliveryId) ?? []).filter((rn) => rn !== rotationNumber);
        if (filtered.length === 0) next.delete(deliveryId);
        else next.set(deliveryId, filtered);
        return next;
      });
      return;
    }
    // 勤務ごとにグループが分かれているので、その勤務の行から探す。
    // truckId で探すと、同じ truck の別の勤務の回転を消してしまう
    const rowsOfDelivery = getRowGroups()
      .flatMap((g) => g.rows)
      .filter((r) => (r.source.extraOfDeliveryId ?? r.source.deliveryId) === deliveryId);
    const targetRow = rowsOfDelivery.find((r) => r.rotationNumber === rotationNumber);
    if (!targetRow) return;
    const legRunIds = getMatchedLegRunIdsInRow(targetRow.source);

    // 案件なし placeholder はローカル非表示のみ。
    if (legRunIds.length === 0) {
      setHiddenPlaceholderRotations((prev) => {
        const next = new Map(prev);
        const current = new Set(next.get(deliveryId) ?? []);
        current.add(rotationNumber);
        next.set(deliveryId, current);
        return next;
      });
      notify(`${rotationNumber}回転目を非表示にしました`, 'success');
      return;
    }

    // 案件ありは確認 → resetting（未割当へ戻す）。
    confirm({
      title: 'この回転を削除しますか？',
      description: `${rotationNumber}回転目を削除します。${legRunIds.length} 件の案件が未割当に戻ります。`,
      onConfirm: async () => {
        try {
          await resetLegRuns(legRunIds);
          notify(`${rotationNumber}回転目を削除し ${legRunIds.length} 件の案件を未割当に戻しました`, 'success');
        } catch (e) {
          const err = e as AxiosError<{ message: string }>;
          notify(err.response?.data?.message ?? '処理に失敗しました', 'error');
        }
      },
    });
  }, [extraRotations, getRowGroups, resetLegRuns, confirm, notify]);

  return { sourceRows, applyHiddenFilter, handleAddRotation, handleDeleteRotation };
}
